import { ANALYTICS } from "../actions/analyticsManager.actions";
import { combinedAnalyticsHandlers, combinedActionHandlers } from "../events";
import { hasAnalyticsCookiesConsent } from "../tracking/onetrust";
import Tracker from "../tracking/tracker";
import {
  TrackPayload,
  TrackingVendor,
  ActionTrackData,
  ActionHandlerReturn,
} from "../types";
import { getDefaultData } from "../utils";

/*
 * Get data and tracking vendor for each Event of Track redux action.
 */
const getDataAndTrackerForAnalyticsEvent = (
  trackPayload: TrackPayload,
  reduxState: any
): [any, TrackingVendor] => {
  const trackParams = trackPayload.params ?? {};
  const trackingVendor = trackPayload.vendor || "all";

  // Event Handlers are optional. They can be used to add additional data (from Redux) to event.
  const analyticsHandler = combinedAnalyticsHandlers[trackPayload.event];
  if (analyticsHandler) {
    const eventData = analyticsHandler(reduxState, trackParams);
    return [eventData, trackingVendor];
  }
  return [trackParams, trackingVendor];
};

/*
 * Get data and tracking vendor for any Redux action we want to track.
 * It returns null by default to mark the action as not to be tracked.
 */
const getDataAndTrackerForAction = (
  action: any,
  reduxState: any
): ActionHandlerReturn => {
  const actionHandler = combinedActionHandlers[action.type];
  if (actionHandler) {
    return actionHandler(reduxState, action.payload);
  }
  return null;
};

const prepareAndSendTrackingData = (action, reduxState) => {
  if (action.type === ANALYTICS) {
    // All ANALYTICS actions are tracked.
    const defaultData = getDefaultData(reduxState);
    const [eventData, trackingVendor] = getDataAndTrackerForAnalyticsEvent(
      action.payload,
      reduxState
    );
    const trackData = { ...defaultData, ...eventData };
    Tracker.send(action.payload.event, trackData, trackingVendor);
  } else {
    const result = getDataAndTrackerForAction(action, reduxState);
    if (result === null) {
      // Do not track the action if result is null
      return;
    }

    const defaultData = getDefaultData(reduxState);

    // result can be an array of array if we are sending multiple events for single action.
    if (Array.isArray(result[0])) {
      result.forEach((r) => {
        const [
          eventName,
          actionTrackData,
          trackingVendor,
        ] = r as ActionTrackData;
        const trackData = { ...defaultData, ...actionTrackData };
        Tracker.send(eventName, trackData, trackingVendor);
      });
    } else {
      const [
        eventName,
        actionTrackData,
        trackingVendor,
      ] = result as ActionTrackData;
      const trackData = { ...defaultData, ...actionTrackData };
      Tracker.send(eventName, trackData, trackingVendor);
    }
  }
};

const middleware = ({ getState }) => {
  return (next) => (action) => {
    if (hasAnalyticsCookiesConsent()) {
      prepareAndSendTrackingData(action, getState());
    }

    // Call the next dispatch method in the middleware chain.
    const returnValue = next(action);
    return returnValue;
  };
};

export default middleware;
